/**
 * Agent contribution registry — runtime implementation.
 *
 * Modules register their agent contributions (history renderers, todo renderers,
 * context actions, entity resolvers, draft handlers) through this registry.
 * The AgentPanel host resolves renderers through this registry instead of
 * hardcoding module-specific branches.
 */

import type {
  AgentHistoryBlock,
  AgentHistoryRendererRegistration,
  AgentTodoItem,
  AgentTodoRendererRegistration,
  ModuleAgentContribution,
  AgentContextAction,
  AgentEntityContextResolver,
  AllowlistTarget,
  AppRuntime,
  EntityRendererRegistration,
} from "../contracts";
import { normalizeSchemaId } from "../../components/shared/entityHref";

export class AgentContributionRegistry {
  private readonly contributions = new Map<string, ModuleAgentContribution>();

  register(moduleId: string, contribution: ModuleAgentContribution): () => void {
    this.contributions.set(moduleId, contribution);
    return () => { this.contributions.delete(moduleId); };
  }

  resolveHistoryRenderer(block: AgentHistoryBlock): AgentHistoryRendererRegistration | null {
    let bestMatch: AgentHistoryRendererRegistration | null = null;
    let bestPriority = -Infinity;

    for (const [, contribution] of this.contributions) {
      if (!contribution.historyRenderers) continue;
      for (const renderer of contribution.historyRenderers) {
        if (renderer.match(block)) {
          const priority = renderer.priority ?? 0;
          if (priority > bestPriority) {
            bestMatch = renderer;
            bestPriority = priority;
          }
        }
      }
    }

    return bestMatch;
  }

  resolveTodoRenderer(item: AgentTodoItem): AgentTodoRendererRegistration | null {
    for (const [, contribution] of this.contributions) {
      if (!contribution.todoRenderers) continue;
      const match = contribution.todoRenderers.find((r) => r.kind === item.kind);
      if (match) return match;
    }
    return null;
  }

  resolveSystemPrompt(moduleId: string): string | undefined {
    return this.contributions.get(moduleId)?.systemPrompt;
  }

  getContextActions(moduleId: string): readonly AgentContextAction[] {
    return this.contributions.get(moduleId)?.contextActions ?? [];
  }

  getEntityContextResolvers(): readonly AgentEntityContextResolver[] {
    const resolvers: AgentEntityContextResolver[] = [];
    for (const [, contribution] of this.contributions) {
      if (contribution.entityContextResolvers) {
        resolvers.push(...contribution.entityContextResolvers);
      }
    }
    return resolvers;
  }

  resolveEntityRenderer(schemaId: string): EntityRendererRegistration | null {
    // Try both original and normalized (DB uses underscores, renderers use dots)
    const variants = [schemaId, normalizeSchemaId(schemaId)];
    for (const sid of variants) {
      for (const [, contribution] of this.contributions) {
        if (!contribution.entityRenderers) continue;
        for (const reg of contribution.entityRenderers) {
          const matches =
            typeof reg.schemaMatch === "function"
              ? reg.schemaMatch(sid)
              : sid === reg.schemaMatch;
          if (matches) return reg;
        }
      }
    }
    return null;
  }

  navigateToEntity(
    schemaId: string,
    entityId: string,
    data: Readonly<Record<string, unknown>>,
    runtime: AppRuntime,
    navigate: (moduleId: string, entityType?: string, entityId?: string) => void,
  ): boolean {
    const reg = this.resolveEntityRenderer(schemaId);
    if (!reg) return false;
    const contribution = this.contributions.get(reg.moduleId);
    if (contribution?.navigateToEntity) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      contribution.navigateToEntity(entityId, schemaId, data, runtime, navigate);
    } else {
      // Default: derive entityType from schemaId (e.g. "contacts.person" → "person")
      const dotIdx = schemaId.indexOf(".");
      const entityType = dotIdx >= 0 ? schemaId.slice(dotIdx + 1) : undefined;
      navigate(reg.moduleId, entityType, entityId);
    }
    return true;
  }

  resolveAllowlistTarget(toolCall: { name: string; args: unknown }): AllowlistTarget | null {
    for (const [, contribution] of this.contributions) {
      if (!contribution.extractAllowlistTarget) continue;
      const result = contribution.extractAllowlistTarget(toolCall);
      if (result) return result;
    }
    return null;
  }

  handleDraftRequest(targetModuleId: string, payload: unknown, runtime: AppRuntime): boolean {
    const contribution = this.contributions.get(targetModuleId);
    if (contribution?.onDraftRequest) {
      contribution.onDraftRequest(payload, runtime);
      return true;
    }
    return false;
  }

  hasContribution(moduleId: string): boolean {
    return this.contributions.has(moduleId);
  }

  listModuleIds(): string[] {
    return Array.from(this.contributions.keys());
  }
}
